import { type CognitoSignedUserState } from '../../reducers/cognitoUserReducer';
import { type TlbSignedUserState } from '../../reducers/tlbUserReducer';

// Admin-only "view as" mode: the app renders with another user's identity while every
// backend call keeps using the admin's own Cognito tokens. Nothing here grants extra
// rights on the backend - it only swaps what the UI thinks the signed in user is.
//
// The record is read once at module load and never mutated afterwards; starting and
// stopping impersonation always goes through a full page reload (see impersonationControl),
// so every cache, datasource and syncer starts from a clean state.

const LOCAL_STORAGE_KEY = 'alol_impersonation';

// kept as a local literal so this module needs no runtime import from the reducers
// (they import from here, and a cycle would break their module init order)
const SIGNED_OUT = 'signed-out' as const;

export type ImpersonatedTlbUserState = TlbSignedUserState | typeof SIGNED_OUT;

export type ImpersonationRecord = {
    version: 1;
    startedAt: number;
    realUserId: string;
    targetUserId: string;
    targetLabel: string;
    cognito: CognitoSignedUserState;
    tlbUser: ImpersonatedTlbUserState;
};

export const IMPERSONATION_TLB_USER_SIGNED_OUT = SIGNED_OUT;

export const IMPERSONATION_READ_ONLY_MESSAGE =
    'Impersonace je read-only, zápis do backendu byl zablokován. Ukonči impersonaci a zopakuj akci pod vlastní identitou.';

const record: ImpersonationRecord | null = readRecord();

export const getImpersonation = (): ImpersonationRecord | null => record;

export const isImpersonating = (): boolean => record !== null;

export const getImpersonatedCognitoState = (): CognitoSignedUserState | null => record?.cognito ?? null;

export const getImpersonatedTlbUserState = (): ImpersonatedTlbUserState | null => record?.tlbUser ?? null;

export const persistImpersonation = (newRecord: ImpersonationRecord): void => {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newRecord));
};

export const clearImpersonationRecord = (): void => {
    window.localStorage.removeItem(LOCAL_STORAGE_KEY);
};

/**
 * Úložiště, nebo `null` tam, kde žádné není. Impersonace je čistě browserová věc, ale tenhle modul
 * se importuje i mimo prohlížeč — pod vitest projektem `unit` (prostředí Node) `window` neexistuje.
 * A protože se `readRecord()` volá při načtení modulu (viz `record` výš), sáhnutí na `window` tam
 * neshodí jen impersonaci, ale IMPORT celého modulu a s ním každý testovací soubor, který ho má
 * kdekoli v grafu závislostí — i když s impersonací nemá co do činění.
 */
function localStorageOrNull(): Storage | null {
    return typeof window === 'undefined' ? null : window.localStorage;
}

function readRecord(): ImpersonationRecord | null {
    const storage = localStorageOrNull();
    if (!storage) {
        return null;
    }

    try {
        const stored = storage.getItem(LOCAL_STORAGE_KEY);
        if (!stored) {
            return null;
        }
        const parsed: ImpersonationRecord = JSON.parse(stored);
        if (parsed?.version !== 1 || !parsed.targetUserId || !parsed.cognito?.groups) {
            storage.removeItem(LOCAL_STORAGE_KEY);

            return null;
        }
        console.warn(`IMPERSONATION ACTIVE: rendering as ${parsed.targetLabel} (${parsed.targetUserId})`);

        return parsed;
    } catch (e) {
        console.error('Error reading impersonation state, dropping it', e);
        // Úklid, který sám nesmí hodit: tenhle catch je poslední záchrana, a když z něj vyletí
        // další výjimka, vezme s sebou načtení modulu. Přesně to se dělo, dokud se tu sahalo
        // rovnou na `window` — v Node ReferenceError uvnitř `try` spadl sem a tady se zopakoval
        // už bez záchytu.
        try {
            storage.removeItem(LOCAL_STORAGE_KEY);
        } catch (cleanupError) {
            console.error('Could not drop the impersonation state either', cleanupError);
        }

        return null;
    }
}
